// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Left orange accent stripe for Filters section
// - Inputs/select: soft blue border + orange focus
// - Buttons: primary orange, outlined blue
// - Tables: blue header, soft zebra rows, hover tint, subtle borders
// - No business/API logic changed

import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  Divider,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { API_URL, API_UPLOAD_URL } from "./config";

const BacklogReport = () => {
  const navigate = useNavigate();

  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
    whiteSpace: "nowrap",
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
    whiteSpace: "nowrap",
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.8 },
  });

  // ===== Original state/logic (UNCHANGED) =====
  const [searchTerm, setSearchTerm] = useState("");
  const [subjectCodes, setSubjectCodes] = useState([]);
  const [backlogData, setBacklogData] = useState([]);
  const [selectedSubjectCode, setSelectedSubjectCode] = useState("");
  const [error, setError] = useState("");
  const [logincampusCode, setCampusCode] = useState("");
  const token = localStorage.getItem("auth");

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        const campusCode = decodedToken.campusCode;
        setCampusCode(campusCode);
      } catch (err) {
        console.error("Error decoding token:", err);
        setCampusCode("");
      }
    }
  }, [token]);

  useEffect(() => {
    const fetchSubjectCodes = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/DataSubjectCodes?CampusCode=${encodeURIComponent(logincampusCode)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data && Array.isArray(response.data)) {
          setSubjectCodes(response.data);
        } else {
          setError("Invalid response for subject codes.");
        }
      } catch (err) {
        console.error("Error fetching subject codes:", err);
        setError("Failed to fetch subject codes.");
      }
    };

    if (logincampusCode) fetchSubjectCodes();
  }, [logincampusCode, token]);

  const fetchBacklogData = async () => {
    if (!searchTerm && !selectedSubjectCode) {
      setBacklogData([]);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/ReportBacklogByStudent`, {
        params: {
          search:
            searchTerm ||
            (selectedSubjectCode === "ALL" ? "" : selectedSubjectCode) ||
            "",
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      setBacklogData(response.data || []);
      setError("");
    } catch (err) {
      console.error("Error fetching backlog data:", err);
      setError("Failed to fetch backlog data.");
    }
  };

  useEffect(() => {
    if (logincampusCode) fetchBacklogData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedSubjectCode, logincampusCode]);

  const handleSearch = (e) => setSearchTerm(e.target.value);

  const handleSubjectCodeChange = (e) => {
    const fullValue = e.target.value;
    const extractedCode =
      fullValue === "ALL"
        ? "ALL"
        : fullValue.match(/<([^>]+)>/)?.[1] || "";
    setSelectedSubjectCode(extractedCode);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedSubjectCode("");
    setBacklogData([]);
  };

  const handleBackToHome = () => navigate("/home");

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Student Backlog
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Search by student details or filter by subject
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* ✅ Filters section */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Filters
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  label="Search by Student ID, Name, or Subject Code"
                  value={searchTerm}
                  onChange={handleSearch}
                  fullWidth
                  size="small"
                  sx={{ ...inputSx, minWidth: 280, flex: 1 }}
                />

                <Select
                  value={
                    subjectCodes.find((subject) =>
                      subject.includes(`<${selectedSubjectCode}>`)
                    ) || (selectedSubjectCode === "ALL" ? "ALL" : "")
                  }
                  onChange={handleSubjectCodeChange}
                  displayEmpty
                  fullWidth
                  size="small"
                  sx={{ ...inputSx, minWidth: 260, flex: 1 }}
                >
                  <MenuItem value="">
                    <em>Select Subject</em>
                  </MenuItem>
                  <MenuItem value="ALL">
                    <em>All Subjects</em>
                  </MenuItem>
                  {subjectCodes.map((subject, index) => (
                    <MenuItem key={index} value={subject}>
                      {subject}
                    </MenuItem>
                  ))}
                </Select>

                <Button variant="contained" sx={primaryBtnSx} onClick={handleResetFilters}>
                  Reset Filters
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Error message */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                mt: 2,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {/* ✅ Table wrapper */}
          <Paper elevation={0} sx={tableWrapSx}>
              {/* INNER – ONLY for horizontal scroll */}
              <Box
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                }}
              >
            <TableContainer component="div">
            <Box sx={{ p: 2 }}>
              {backlogData.length === 0 ? (
                <Typography sx={{ textAlign: "center", py: 3, opacity: 0.8 }}>
                  No data to display
                </Typography>
              ) : (
                <>
                  <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                    Total Backlog Student Count: {backlogData.length}
                  </Typography>

                  <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                      <TableRow sx={theadRowSx}>
                        <TableCell>#</TableCell>
                        <TableCell>Student ID</TableCell>
                        <TableCell>Full Name</TableCell>
                        <TableCell>Number of Backlogs</TableCell>
                        <TableCell>Current Semester</TableCell>
                        <TableCell>Backlog Subjects</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {backlogData.map((row, index) => (
                        <TableRow key={index} sx={zebraRowSx(index)}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{row.StudentID}</TableCell>
                          <TableCell>{row.FullName}</TableCell>
                          <TableCell>{row.NumberOfBacklogs}</TableCell>
                          <TableCell>{row.CurrentSemester}</TableCell>
                          <TableCell>{row.BacklogSubjects}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </Box>
          </TableContainer>
          </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default BacklogReport;
